// AgentVerse/client/ui/ProjectParticipants.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../helpers/avatarHelper.js";

export class ProjectParticipants extends ComponentBase {
 static styles = `
        .project-participants-view {
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        .participants-header {
            padding: 0.75rem 1.5rem;
            border-bottom: 1px solid var(--bs-border-color);
            flex-shrink: 0;
            background-color: var(--bs-body-bg); /* Added for consistency */
        }
        .participants-header h3 {
            margin: 0;
        }
        .participants-main-content {
            flex-grow: 1;
            display: flex;
            overflow: hidden; /* Parent handles scroll if needed, sub-panels scroll themselves */
        }
        .current-participants-panel, .add-participants-panel {
            padding: 1rem;
            display: flex;
            flex-direction: column;
            flex: 1 1 50%; /* Equal width distribution */
            overflow-y: auto; /* Enable scrolling for individual panels */
            background-color: var(--bs-body-bg); /* Consistent background */
        }
        .current-participants-panel {
            border-right: 1px solid var(--bs-border-color);
        }
        .participants-list, .search-results-list {
            list-style: none;
            padding: 0;
            margin-top: 0.5rem; /* Space above lists */
        }
        .participant-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.5rem;
            border-radius: var(--bs-border-radius);
            margin-bottom: 0.25rem;
            transition: background-color 0.2s;
        }
        .participant-item:hover {
            background-color: var(--bs-tertiary-bg);
        }
        .participant-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            overflow: hidden;
        }
        .participant-name {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .participant-origin {
            font-size: 0.8rem;
            color: var(--bs-secondary-color);
        }
        .remove-participant-btn.owner-protected {
            opacity: 0.5;
            cursor: not-allowed;
        }
        .search-result-item .add-btn { /* Style for add button if needed */
            opacity: 0;
            transition: opacity 0.2s;
        }
        .search-result-item:hover .add-btn {
            opacity: 1;
        }
        .add-participants-search-input {
            margin-bottom: 0.5rem; /* Space below search input */
        }
        .panel-subtitle { /* For "Current Participants" and "Add Participants" */
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: var(--bs-emphasis-color);
        }
        /* --- NEW: Responsive Styles for Mobile View --- */
        @media (max-width: 767.98px) {
            .participants-main-content {
                flex-direction: column; /* Stack the two panels vertically */
                overflow-y: auto;       /* The main content area becomes scrollable */
            }

            .current-participants-panel, .add-participants-panel {
                flex: 1 1 auto;         /* Allow panels to grow/shrink vertically */
                overflow-y: visible;    /* Disable individual scrolling; let the parent handle it */
            }

            .current-participants-panel {
                border-right: none;     /* Remove the vertical border */
                border-bottom: 1px solid var(--bs-border-color); /* Add a horizontal border */
                padding-bottom: 1.5rem; /* Add some spacing */
            }
        }
    `;
    constructor(config) {
        super(config);
        this.projectId = null;
        this.projectOwnerId = null;
        this.participants = { contacts: [], tags: [] };
        this.allAvailableContacts = []; // All contacts available globally for adding
        this.addParticipantsSearchResults = [];
        this.isDirty = false;
        
        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this);
        this._boundHandleMessagingContactChange = this.handleMessagingContactChange.bind(this);
        this._debouncedSearch = this.debounce(this.searchParticipants.bind(this), 300);
        
        this.container = null;
        this.headerElement = null;
        this.mainContentElement = null;
        this.currentParticipantsPanelElement = null;
        this.addParticipantsPanelElement = null;
        this.addParticipantsSearchInputElement = null;
        this.addParticipantsResultsListElement = null;

        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.local.on('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.on('messagingContactCreated', this._boundHandleMessagingContactChange);
        this.appClient.events.server.on('messagingContactUpdated', this._boundHandleMessagingContactChange);
        this.appClient.events.server.on('messagingContactDeleted', this._boundHandleMessagingContactChange);
        await super.initialize();
    }
    
    async initAfterLogin() {
        const selectedProject = this.appClient.getSharedData('selectedProject');
        if (selectedProject) {
            await this.handleProjectSelected({ projectData: selectedProject });
        } else {
            // If no project selected, ensure UI reflects this (render() handles this)
            if (this.container) await this.render();
        }
    }

    async handleProjectSelected({ projectData }) {
        const newProjectId = projectData ? projectData._id : null;
        if (this.projectId === newProjectId) return;

        this.projectId = newProjectId;
        this.projectOwnerId = projectData ? projectData.ownerId : null;
        
        if (!this.projectId) {
            this.participants = { contacts: [], tags: [] };
            this.allAvailableContacts = [];
            this.addParticipantsSearchResults = [];
            this.isDirty = false;
        } else {
            await this.loadData(); // Fetches current participants and all available contacts
        }
        
        if (this.container) {
            await this.render(); // Full re-render on project change
        }
    }

    async handleMessagingContactChange(eventData) {
        this.log.debug("Messaging contact change detected, refreshing addable participant options.", eventData);
        if (!this.projectId) return; // Only refresh if a project is active

        await this._loadAllAvailableContacts(); // Re-fetch all contacts
        
        // If search input exists and has value, re-filter and re-render search results
        if (this.addParticipantsSearchInputElement && this.addParticipantsSearchInputElement.value) {
            await this.searchParticipants(this.addParticipantsSearchInputElement.value);
        } else {
            // If no search term, clear current search results and re-render (likely empty or showing all available)
            this.addParticipantsSearchResults = [];
            this.renderAddParticipantsResultsList(); 
        }
    }

    async _loadAllAvailableContacts() {
        // Contacts are global, so we don't filter by projectId here.
        try {
            const contactsResult = await this.appClient.rpcClient.call(
                'MessagingContactsRpc.getData', 
                { filter: { isArchived: false }, pageSize: 2000 }, // Fetch non-archived global contacts
                {} // No project context needed for fetching global contacts
            );
            this.allAvailableContacts = contactsResult.data || [];
        } catch (error) {
            this.log.error("Failed to load all available contacts:", error);
            this.allAvailableContacts = [];
        }
    }

    async loadData() {
        if (!this.projectId) return;
        try {
            this.participants = await this.appClient.rpcClient.call('ProjectRpc.getProjectParticipants', { projectId: this.projectId });
            await this._loadAllAvailableContacts();
            this.isDirty = false;
        } catch (error) {
            this.log.error("Failed to load project participants data:", error);
            this.appClient.notifier.error(this.t("Could not load participant data."));
        }
    }

    async searchParticipants(term) {
        if (!this.projectId) {
            this.addParticipantsSearchResults = [];
            this.renderAddParticipantsResultsList();
            return;
        }
        
        const currentParticipantContactIds = new Set(this.participants.contacts);
        const currentParticipantTags = new Set(this.participants.tags);

        if (!term || term.length < 1) {
            // Show all available, non-participant contacts and tags
            this.addParticipantsSearchResults = [
                ...this.allAvailableContacts
                    .filter(contact => !currentParticipantContactIds.has(contact._id.toString()))
                    .map(c => ({ value: c._id.toString(), label: c.displayName, type: 'contact', avatarUrl: c.avatarUrl })),
                // Add tag options if you have a way to fetch all available tags
                // For now, we'll rely on ProjectRpc.getParticipantOptions if search term is present
            ];
            this.renderAddParticipantsResultsList();
            return;
        }
        
        try {
            const options = await this.appClient.rpcClient.call('ProjectRpc.getParticipantOptions', { projectId: this.projectId });
            const lowerTerm = term.toLowerCase();
            
            this.addParticipantsSearchResults = options.filter(opt => 
                opt.label.toLowerCase().includes(lowerTerm) &&
                (opt.type === 'tag' ? !currentParticipantTags.has(opt.value) : !currentParticipantContactIds.has(opt.value))
            );
            this.renderAddParticipantsResultsList();
        } catch (error) {
            this.log.error("Failed to search participants via RPC:", error);
            this.addParticipantsSearchResults = [];
            this.renderAddParticipantsResultsList();
        }
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'project-participants-view';

            this.headerElement = document.createElement('div');
            this.headerElement.className = 'participants-header d-flex justify-content-between align-items-center';
            this.container.appendChild(this.headerElement);

            this.mainContentElement = document.createElement('div');
            this.mainContentElement.className = 'participants-main-content';
            this.container.appendChild(this.mainContentElement);

            this.currentParticipantsPanelElement = document.createElement('div');
            this.currentParticipantsPanelElement.className = 'current-participants-panel';
            this.mainContentElement.appendChild(this.currentParticipantsPanelElement);
            
            this.addParticipantsPanelElement = document.createElement('div');
            this.addParticipantsPanelElement.className = 'add-participants-panel';
            this.mainContentElement.appendChild(this.addParticipantsPanelElement);
            // Create the static structure of the "Add Participants" panel once
            this.renderAddParticipantsPanelStructure(); 
        }

        // --- Update Header Dynamically ---
        this.headerElement.innerHTML = `<h3><i class="bi bi-people-fill me-2"></i> ${this.t('Project Participants')}</h3>`;
        const saveButton = document.createElement('button');
        saveButton.id = `${this.config.instanceId}-save-btn`;
        saveButton.className = 'btn btn-primary';
        saveButton.innerHTML = `<i class="bi bi-save me-2"></i> ${this.t('Save Changes')}`;
        saveButton.disabled = !this.isDirty;
        saveButton.addEventListener('click', () => this.saveChanges());
        this.headerElement.appendChild(saveButton);

        // --- Render Dynamic Content ---
        if (!this.projectId) {
            this.currentParticipantsPanelElement.innerHTML = ''; // Clear panel
            this.addParticipantsPanelElement.style.display = 'none'; // Hide add panel
            // Show placeholder in main container
            this.mainContentElement.innerHTML = ''; // Clear existing panels first
            const placeholder = document.createElement('div');
            placeholder.className = 'alert alert-info text-center m-3 flex-grow-1'; // Use flex-grow-1
            placeholder.innerHTML = `<i class="bi bi-info-circle-fill me-2"></i> ${this.t('Please select a project to manage its participants.')}`;
            this.mainContentElement.appendChild(placeholder);
        } else {
            if (this.addParticipantsPanelElement) this.addParticipantsPanelElement.style.display = ''; // Ensure add panel is visible
            // If mainContent was showing placeholder, clear it and re-add panels
            if (!this.mainContentElement.contains(this.currentParticipantsPanelElement)) {
                this.mainContentElement.innerHTML = '';
                this.mainContentElement.appendChild(this.currentParticipantsPanelElement);
                this.mainContentElement.appendChild(this.addParticipantsPanelElement);
            }
            this.renderCurrentParticipantsPanel();
            this.renderAddParticipantsResultsList(); // Only updates the list inside the panel
        }

        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    renderCurrentParticipantsPanel() {
        if (!this.currentParticipantsPanelElement) return;
        this.currentParticipantsPanelElement.innerHTML = `<h5 class="panel-subtitle">${this.t('Current Participants')}</h5>`;
        const list = document.createElement('ul');
        list.className = 'participants-list';
        this.currentParticipantsPanelElement.appendChild(list);

        (this.participants.tags || []).forEach(tag => list.appendChild(this.createParticipantItem(tag, 'tag')));
        
        (this.participants.contacts || []).forEach(contactId => {
            const contact = this.allAvailableContacts.find(c => c._id.toString() === contactId);
            if (contact) {
                list.appendChild(this.createParticipantItem(contact, 'contact'));
            } else {
                // Potentially display a placeholder if contact details are missing
                this.log.warn(`Contact details for ID ${contactId} not found in allAvailableContacts.`);
            }
        });

        if (list.childElementCount === 0) {
            list.innerHTML = `<p class="text-muted small p-2">${this.t('No participants added yet.')}</p>`;
        }
        this.appClient.tx.applyTranslations(this.currentParticipantsPanelElement);
    }

    createParticipantItem(item, type) {
        const li = document.createElement('li');
        li.className = 'participant-item';

        const infoDiv = document.createElement('div');
        infoDiv.className = 'participant-info';
        
        let avatarHtml, name, origin;

        if (type === 'tag') {
            avatarHtml = `<span class="app-avatar app-avatar-small bg-secondary"><i class="bi bi-tag-fill"></i></span>`;
            name = item; // item is the tag string
            origin = this.t('Added via Tag');
        } else { // type === 'contact'
            avatarHtml = createAvatarElement({ displayName: item.displayName, avatarUrl: item.avatarUrl, sizeClass: 'small' }).outerHTML;
            name = item.displayName;
            origin = this.t('Directly Added');
        }

        infoDiv.innerHTML = `
            ${avatarHtml}
            <div>
                <div class="participant-name">${name}</div>
                <div class="participant-origin">${origin}</div>
            </div>
        `;
        
        const removeButton = document.createElement('button');
        removeButton.className = 'btn btn-sm btn-outline-danger remove-participant-btn';
        removeButton.innerHTML = `<i class="bi bi-x-lg"></i>`;
        removeButton.title = this.t("Remove");

        let isOwner = false;
        if (type === 'contact' && this.projectOwnerId) {
            const ownerContact = this.allAvailableContacts.find(c => c.contactType === 'internal_user' && c.appUserId === this.projectOwnerId);
            if (ownerContact && ownerContact._id.toString() === item._id.toString()) {
                isOwner = true;
            }
        }
        
        if (isOwner) {
            removeButton.disabled = true;
            removeButton.classList.add('owner-protected');
            removeButton.title = this.t("Project owner cannot be removed.");
        } else {
            removeButton.addEventListener('click', () => {
                if (type === 'tag') {
                    this.participants.tags = this.participants.tags.filter(t => t !== item);
                } else {
                    this.participants.contacts = this.participants.contacts.filter(id => id !== item._id.toString());
                }
                this.isDirty = true;
                this.updateSaveButtonState();
                this.renderCurrentParticipantsPanel();
                this.searchParticipants(this.addParticipantsSearchInputElement?.value || '');
            });
        }

        li.appendChild(infoDiv);
        li.appendChild(removeButton);
        return li;
    }

    renderAddParticipantsPanelStructure() {
        if (!this.addParticipantsPanelElement) return;
        // This method sets up the static parts of the "Add Participants" panel.
        // It's called once when the main container is created.
        this.addParticipantsPanelElement.innerHTML = `
            <h5 class="panel-subtitle">${this.t('Add Participants')}</h5>
            <input type="search" class="form-control add-participants-search-input" placeholder="${this.t('Search by name or tag...')}">
            <div class="search-results-list list-group list-group-flush"></div>
        `;
        
        this.addParticipantsSearchInputElement = this.addParticipantsPanelElement.querySelector('.add-participants-search-input');
        this.addParticipantsResultsListElement = this.addParticipantsPanelElement.querySelector('.search-results-list');
        
        if (this.addParticipantsSearchInputElement) {
            this.addParticipantsSearchInputElement.addEventListener('input', (e) => this._debouncedSearch(e.target.value));
        }
        this.appClient.tx.applyTranslations(this.addParticipantsPanelElement);
    }

    renderAddParticipantsResultsList() {
        if (!this.addParticipantsResultsListElement) return;
        this.addParticipantsResultsListElement.innerHTML = ''; // Clear previous results

        if (this.addParticipantsSearchResults.length === 0 && this.addParticipantsSearchInputElement?.value) {
            this.addParticipantsResultsListElement.innerHTML = `<p class="text-muted small p-2">${this.t('No results found.')}</p>`;
        } else if (this.addParticipantsSearchResults.length === 0 && (!this.addParticipantsSearchInputElement || !this.addParticipantsSearchInputElement.value)) {
            this.addParticipantsResultsListElement.innerHTML = `<p class="text-muted small p-2">${this.t('Type to search for contacts or tags to add.')}</p>`;
        } else {
            this.addParticipantsSearchResults.forEach(result => {
                const isContact = result.type === 'contact';
                const avatarHtml = isContact 
                    ? createAvatarElement({ displayName: result.label, avatarUrl: result.avatarUrl, sizeClass: 'small' }).outerHTML
                    : `<span class="app-avatar app-avatar-small bg-secondary"><i class="bi bi-tag-fill"></i></span>`;
                
                const itemElement = document.createElement('div'); // Use div for list-group-item
                itemElement.className = 'participant-item search-result-item list-group-item list-group-item-action'; 
                itemElement.innerHTML = `
                    <div class="participant-info">
                        ${avatarHtml}
                        <span>${result.label}</span>
                    </div>
                    <button class="btn btn-sm btn-success add-btn"><i class="bi bi-plus-lg"></i></button>
                `;
                itemElement.querySelector('.add-btn').addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent click on list item itself
                    if (isContact) {
                        // --- FIX START ---
                        // The search result might be more up-to-date than our cached `allAvailableContacts`.
                        // If we add a contact from search, we must ensure its details are available for rendering
                        // in the "Current Participants" list.
                        const contactExistsInCache = this.allAvailableContacts.some(c => c._id.toString() === result.value);
                        if (!contactExistsInCache) {
                            this.log.warn(`Contact "${result.label}" found via search but not in local cache. Adding a representation for immediate rendering.`);
                            // Create a temporary contact object from the search result data.
                            // This is enough for `createParticipantItem` to render it correctly.
                            // The full object will be loaded on the next full refresh.
                            this.allAvailableContacts.push({ _id: result.value, displayName: result.label, avatarUrl: result.avatarUrl });
                        }
                        // --- FIX END ---

                        if (!this.participants.contacts.includes(result.value)) {
                            this.participants.contacts.push(result.value);
                        }
                    } else { // tag
                        if (!this.participants.tags.includes(result.value)) {
                            this.participants.tags.push(result.value);
                        }
                    }
                    this.isDirty = true;
                    this.updateSaveButtonState();
                    this.renderCurrentParticipantsPanel();
                    this.searchParticipants(this.addParticipantsSearchInputElement?.value || '');
                });
                this.addParticipantsResultsListElement.appendChild(itemElement);
            });
        }
        this.appClient.tx.applyTranslations(this.addParticipantsResultsListElement);
    }

    updateSaveButtonState() {
        const saveBtn = this.container?.querySelector(`#${this.config.instanceId}-save-btn`);
        if (saveBtn) {
            saveBtn.disabled = !this.isDirty;
        }
    }

    async saveChanges() {
        if (!this.isDirty || !this.projectId) return;
        const saveBtn = this.container?.querySelector(`#${this.config.instanceId}-save-btn`);
        if(saveBtn) {
            saveBtn.disabled = true; 
            saveBtn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${this.t('Saving...')}`;
            this.appClient.tx.applyTranslations(saveBtn);
        }

        try {
            await this.appClient.rpcClient.call('ProjectRpc.updateProjectParticipants', {
                projectId: this.projectId,
                participants: this.participants
            });
            this.appClient.notifier.success(this.t("Project participants updated successfully!"));
            this.isDirty = false;
        } catch (error) {
            this.log.error("Failed to save project participants:", error);
            this.appClient.notifier.error(this.t("Could not save changes."));
        } finally {
            this.updateSaveButtonState();
            if (saveBtn) {
                 saveBtn.innerHTML = `<i class="bi bi-save me-2"></i> ${this.t('Save Changes')}`;
                 this.appClient.tx.applyTranslations(saveBtn);
            }
        }
    }
    
    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    destroy() {
        this.appClient.events.local.off('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.local.off('messagingContactCreated', this._boundHandleMessagingContactChange);
        this.appClient.events.local.off('messagingContactUpdated', this._boundHandleMessagingContactChange);
        this.appClient.events.local.off('messagingContactDeleted', this._boundHandleMessagingContactChange);
        super.destroy();
    }
}